import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Users, DollarSign, TrendingUp, Pencil, Trash2, Search, Megaphone } from "lucide-react";

interface PartnerStats {
  totalReferrals: number;
  activeReferrals: number;
  totalPaid: number;
}

interface Partner {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  referralCode: string;
  programType?: string;
  isActive: boolean;
  stats: PartnerStats;
}

export default function ReferralPayoutManager() {
  const { toast } = useToast();
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [isPayoutOpen, setIsPayoutOpen] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    amount: '',
    bankDetails: '',
    notes: ''
  });

  const [isEditCodeOpen, setIsEditCodeOpen] = useState(false);
  const [editCodePartner, setEditCodePartner] = useState<Partner | null>(null);
  const [newReferralCode, setNewReferralCode] = useState('');

  const [deletePartner, setDeletePartner] = useState<Partner | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "referral" | "creator">("all");

  const { data: partnersData, isLoading } = useQuery<{ partners: Partner[] }>({
    queryKey: ["/api/admin/referral-partners"],
  });

  const createPayoutMutation = useMutation({
    mutationFn: async (data: { partnerId: string; amount: number; bankDetails: string; notes: string }) => {
      return await apiRequest("POST", "/api/admin/referral-payouts", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/referral-partners"] });
      toast({
        title: "Payout created",
        description: "Payout request has been created successfully",
      });
      setIsPayoutOpen(false);
      setSelectedPartner(null);
      setPayoutForm({ amount: '', bankDetails: '', notes: '' });
    },
    onError: (error: Error) => {
      toast({
        title: "Payout failed",
        description: error.message || "Failed to create payout. Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateCodeMutation = useMutation({
    mutationFn: async ({ id, referralCode }: { id: string; referralCode: string }) => {
      return await apiRequest("PATCH", `/api/admin/referral-partners/${id}/code`, { referralCode });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/referral-partners"] });
      toast({ title: "Referral code updated" });
      setIsEditCodeOpen(false);
      setEditCodePartner(null);
      setNewReferralCode('');
    },
    onError: (error: Error) => {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest("DELETE", `/api/admin/referral-partners/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/referral-partners"] });
      toast({ title: "Partner deleted", description: "Partner account has been removed." });
      setDeletePartner(null);
    },
    onError: (error: Error) => {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
    },
  });

  const handleCreatePayout = () => {
    if (!selectedPartner) return;

    const amount = parseFloat(payoutForm.amount);
    const minPayout = selectedPartner.programType === 'creator' ? 5000 : 2000;
    if (isNaN(amount) || amount < minPayout) {
      toast({
        title: "Invalid amount",
        description: `Minimum payout is $${minPayout.toLocaleString()} JMD`,
        variant: "destructive",
      });
      return;
    }

    createPayoutMutation.mutate({
      partnerId: selectedPartner.id,
      amount,
      bankDetails: payoutForm.bankDetails,
      notes: payoutForm.notes
    });
  };

  const openPayoutDialog = (partner: Partner) => {
    setSelectedPartner(partner);
    const monthlyEarnings = partner.stats.activeReferrals * 300;
    setPayoutForm({
      amount: monthlyEarnings.toString(),
      bankDetails: '',
      notes: ''
    });
    setIsPayoutOpen(true);
  };

  const openEditCode = (partner: Partner) => {
    setEditCodePartner(partner);
    setNewReferralCode(partner.referralCode);
    setIsEditCodeOpen(true);
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading partners...</div>;
  }

  const partners = partnersData?.partners || [];

  const referralCount = partners.filter(p => (p.programType || 'referral') !== 'creator').length;
  const creatorCount = partners.filter(p => p.programType === 'creator').length;

  const filteredPartners = partners.filter((p) => {
    const type = p.programType || 'referral';
    const matchesType = typeFilter === 'all' || type === typeFilter;
    const matchesSearch = searchQuery === '' ||
      `${p.firstName} ${p.lastName} ${p.email} ${p.referralCode}`.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Partners</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{partners.length}</div>
            <p className="text-xs text-muted-foreground">
              {referralCount} referral, {creatorCount} creator
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Referrals</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {partners.reduce((sum, p) => sum + p.stats.totalReferrals, 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {partners.reduce((sum, p) => sum + p.stats.activeReferrals, 0)} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Paid</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${partners.reduce((sum, p) => sum + p.stats.totalPaid, 0).toLocaleString()} JMD
            </div>
            <p className="text-xs text-muted-foreground">
              Lifetime payouts
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Earnings</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${partners.reduce((sum, p) => sum + p.stats.activeReferrals * 300, 0).toLocaleString()} JMD
            </div>
            <p className="text-xs text-muted-foreground">
              Based on active referrals
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Partners</CardTitle>
          <CardDescription>Manage partner accounts, referral codes, payouts, and view earnings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as "all" | "referral" | "creator")}>
              <SelectTrigger className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Partners ({partners.length})</SelectItem>
                <SelectItem value="referral">Referral Partners ({referralCount})</SelectItem>
                <SelectItem value="creator">Content Creators ({creatorCount})</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="space-y-4">
            {filteredPartners.map((partner) => {
              const isCreatorPartner = partner.programType === 'creator';
              const monthlyEarnings = partner.stats.activeReferrals * 300;
              const minPayout = isCreatorPartner ? 5000 : 2000;
              const canPayout = monthlyEarnings >= minPayout;

              return (
                <div key={partner.id} className="p-4 border rounded-md">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold">{partner.firstName} {partner.lastName}</h3>
                        <Badge variant={partner.isActive ? "default" : "secondary"}>
                          {partner.isActive ? "Active" : "Inactive"}
                        </Badge>
                        {isCreatorPartner ? (
                          <Badge className="bg-chart-2 text-white">
                            <Megaphone className="w-3 h-3 mr-1" />
                            Creator
                          </Badge>
                        ) : (
                          <Badge variant="outline">
                            <Users className="w-3 h-3 mr-1" />
                            Referral
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{partner.email}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <p className="text-sm text-muted-foreground">Code: <span className="font-mono font-medium text-foreground">{partner.referralCode}</span></p>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEditCode(partner)}
                          title="Edit referral code"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">{isCreatorPartner ? 'Conversions' : 'Referrals'}</div>
                        <div className="font-semibold">{partner.stats.activeReferrals} / {partner.stats.totalReferrals}</div>
                      </div>

                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">{isCreatorPartner ? 'Conversion Earnings' : 'Monthly Earnings'}</div>
                        <div className="font-semibold">${monthlyEarnings.toLocaleString()} JMD</div>
                      </div>

                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">Total Paid</div>
                        <div className="font-semibold">${partner.stats.totalPaid.toLocaleString()} JMD</div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => openPayoutDialog(partner)}
                          disabled={!canPayout || !partner.isActive}
                          size="sm"
                        >
                          <DollarSign className="w-4 h-4 mr-1" />
                          Payout
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setDeletePartner(partner)}
                          title="Delete partner"
                          className="text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredPartners.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                {searchQuery || typeFilter !== 'all' ? 'No partners match your filters' : 'No partners found'}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={isPayoutOpen} onOpenChange={setIsPayoutOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Payout</DialogTitle>
            <DialogDescription>
              Create a payout for {selectedPartner?.firstName} {selectedPartner?.lastName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount (JMD)</Label>
              <Input
                id="amount"
                type="number"
                min={selectedPartner?.programType === 'creator' ? "5000" : "2000"}
                step="100"
                value={payoutForm.amount}
                onChange={(e) => setPayoutForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="Enter amount"
              />
              <p className="text-sm text-muted-foreground">
                Minimum: ${selectedPartner?.programType === 'creator' ? '5,000' : '2,000'} JMD
                {selectedPartner?.programType === 'creator' && ' (Creator)'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bankDetails">Bank Details</Label>
              <Textarea
                id="bankDetails"
                value={payoutForm.bankDetails}
                onChange={(e) => setPayoutForm(prev => ({ ...prev, bankDetails: e.target.value }))}
                placeholder="Enter bank account details"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={payoutForm.notes}
                onChange={(e) => setPayoutForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add any notes about this payout"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setIsPayoutOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreatePayout}
              disabled={createPayoutMutation.isPending}
            >
              {createPayoutMutation.isPending ? "Creating..." : "Create Payout"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditCodeOpen} onOpenChange={setIsEditCodeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Referral Code</DialogTitle>
            <DialogDescription>
              Change the referral code for {editCodePartner?.firstName} {editCodePartner?.lastName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="newCode">Referral Code</Label>
            <Input
              id="newCode"
              value={newReferralCode}
              onChange={(e) => setNewReferralCode(e.target.value)}
              placeholder="e.g., mycode123"
              minLength={3}
              maxLength={20}
            />
            <p className="text-xs text-muted-foreground">
              3-20 characters. Letters, numbers, hyphens and underscores only.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsEditCodeOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => editCodePartner && updateCodeMutation.mutate({ id: editCodePartner.id, referralCode: newReferralCode })}
              disabled={updateCodeMutation.isPending || !newReferralCode || newReferralCode.length < 3}
            >
              {updateCodeMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletePartner} onOpenChange={(open) => !open && setDeletePartner(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this partner?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{deletePartner?.firstName} {deletePartner?.lastName}</strong>'s account ({deletePartner?.email}), along with all their referral records and payout history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletePartner && deleteMutation.mutate(deletePartner.id)}
              className="bg-destructive text-destructive-foreground"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
